// ApiFieldError names one invalid field on a form, mirroring the
// backend's validation.FieldError wire shape ({field, message}). See
// ApiError.fields below.
export interface ApiFieldError {
	field: string;
	message: string;
}

// ApiError is the canonical error the api client throws on a non-2xx response.
// It extends Error, so `err instanceof Error` and every `.message` read keep
// working — `.message` is preserved verbatim from `body.message || body.error`.
// The addition is `.status`: consumers detecting a specific HTTP status use
// `err instanceof ApiError && err.status === N` (NOT `String(err).includes('403')`,
// which silently no-ops because the client doesn't produce status-prefixed
// messages). See utils/errorMessages.ts `userMessage(err)` for the canonical
// mapping to user-facing copy.
//
// Lives in its own module so api.ts, errorMessages.ts, and any consumer can import
// it without a circular dependency on the (large) api client.
export class ApiError extends Error {
	readonly status: number;
	// Responsive Workstream C — true when this represents a NETWORK-level
	// failure (fetch() itself rejected — offline, DNS, a dropped
	// connection) on a mutating request, as opposed to an HTTP error
	// response. status is 0 in this case; there is no HTTP status to
	// report. Consumers that want to react specifically to "we're
	// offline" (the connection banner, a save action's inline copy)
	// check this explicitly rather than string-matching err.message —
	// the same reasoning as `.status` above: Chrome/Firefox/Safari all
	// word a network failure differently, so a substring check is
	// fragile where an explicit field is not.
	readonly retriableOffline: boolean;
	// The server-422 → field-level error contract: an optional per-field
	// breakdown, populated from the response body's "fields" array when
	// the backend's validation.Accumulator produced one. Additive —
	// `.message` carries the same joined string it always has, and most
	// 422s (and every non-422) still leave this undefined. A future
	// field-binding workstream reads this to mark individual inputs;
	// nothing in this codebase consumes it yet.
	readonly fields?: ApiFieldError[];
	// The optimistic-concurrency 409 contract: the row's CURRENT revision
	// as the server just read it, when a save was rejected because the
	// caller's last-known revision had moved (somebody else saved first).
	// Additive and optional in the same way as `fields` — populated only
	// from a body carrying `conflict: true` (Collections is the first
	// producer; see backend handler/collections.go collectionsErr), so
	// every other 409 leaves it undefined.
	//
	// It exists so a client can offer "reload" or "save anyway" WITHOUT a
	// second round trip to discover what it lost the race to. A consumer
	// must still never auto-resend with force_save — that silently
	// discards the other person's work; the choice belongs to the user.
	readonly currentRevision?: number;
	// FR-60's extension of the same contract for the roster shift editor:
	// the row's current state, and who/when made the change that won the
	// race, so a caller can show what changed rather than just "reload".
	// Additive and optional in the same way as `currentRevision`;
	// populated only from a body carrying `conflict: true` alongside
	// `current` / `changed_by_name` / `changed_at` (backend
	// handler/roster.go shiftConflictErr is the producer). Left as
	// `Record<string, unknown>` here (not RosterShift) so this shared
	// module doesn't need to import roster's types — a consumer casts it.
	readonly current?: Record<string, unknown>;
	readonly changedByName?: string;
	readonly changedAt?: string;
	// The impersonation read-only-write contract (docs/impersonation-plan.md):
	// a write refused because the live impersonation session hasn't opted
	// into "allow changes" comes back 403 with a machine-readable code
	// (`impersonation_read_only`) alongside the human message, so a caller
	// can react to THAT specific case rather than a generic 403 — additive
	// and optional in the same way as `fields`/`currentRevision` above,
	// populated only from a body carrying a "code" string.
	readonly code?: string;
	// Paul's ruling on confirmation (roster copy/apply tools, backend
	// handler/roster.go rosterErr's roster_published_week_confirm case):
	// a 409 naming which already-published week(s) the request would
	// touch and how many shifts it would actually add, so a caller can
	// show that and resend with confirm_published rather than treating
	// this as a hard failure. Additive and optional in the same way as
	// `code`; populated only from a body carrying
	// `code: "roster_published_week_confirm"`.
	readonly publishedWeeks?: string[];
	readonly wouldCreate?: number;
	// The whole parsed response body of a refusal, for a caller that needs a
	// field no other property carries (the 409 of a ticket request names the
	// ticket it already made in `ref`). Additive and optional.
	readonly body?: Record<string, unknown>;
	constructor(
		status: number,
		message: string,
		retriableOffline = false,
		fields?: ApiFieldError[],
		currentRevision?: number,
		code?: string,
		current?: Record<string, unknown>,
		changedByName?: string,
		changedAt?: string,
		publishedWeeks?: string[],
		wouldCreate?: number,
		body?: Record<string, unknown>,
	) {
		super(message);
		this.name = "ApiError";
		this.status = status;
		this.retriableOffline = retriableOffline;
		this.fields = fields;
		this.currentRevision = currentRevision;
		this.code = code;
		this.current = current;
		this.changedByName = changedByName;
		this.changedAt = changedAt;
		this.publishedWeeks = publishedWeeks;
		this.wouldCreate = wouldCreate;
		this.body = body;
		// Restore the prototype chain for instanceof across transpile targets.
		Object.setPrototypeOf(this, ApiError.prototype);
	}
}
